// apps/frontend/src/components/ui/AutocompletePreferences.tsx
// 🎨 Composant Autocomplete Premium pour Préférences - VERSION i18n

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// ============================================================================
// 🎯 TYPES
// ============================================================================

interface ColorItem {
  name: string;
  color: string;
}

interface StyleItem {
  name: string;
  icon: string;
}

interface AutocompletePreferencesProps {
  type: 'brand' | 'color' | 'style';
  selectedItems: string[];
  onAdd: (item: string) => void;
  onRemove: (item: string) => void;
  placeholder: string;
}

// ============================================================================
// 🎨 DATA : 500+ MARQUES + COULEURS + STYLES
// ============================================================================

const ALL_BRANDS = [
  // 🇫🇷 Top Français (25)
  'Zara',
  'H&M',
  'Petit Bateau',
  'Bonpoint',
  'Jacquemus',
  'Sézane',
  'Maje',
  'Sandro',
  'Ba&sh',
  'Comptoir des Cotonniers',
  'Mango',
  'Promod',
  'Caroll',
  'Naf Naf',
  'Pimkie',
  'Etam',
  'Undiz',
  'Damart',
  'Blancheporte',
  'La Redoute',
  'Kiabi',
  "Tape à l'Oeil",
  'Okaidi',
  'Sergent Major',
  'Absorba',

  // 🏃 Sport & Active (40)
  'Nike',
  'Adidas',
  'Puma',
  'New Balance',
  'Reebok',
  'Under Armour',
  'Asics',
  'Vans',
  'Converse',
  'Jordan',
  'Lacoste',
  'Fila',
  'Ellesse',
  'Kappa',
  'Lotto',
  'Umbro',
  'Le Coq Sportif',
  'Champion',
  'Columbia',
  'The North Face',
  'Patagonia',
  'Quiksilver',
  'Billabong',
  'Roxy',
  'Oakley',
  'Decathlon',
  'Domyos',
  'Kalenji',
  'Tribord',
  "Wed'ze",
  'Forclaz',
  'Kipsta',
  'Kimjaly',
  'Nyamba',
  'Olaian',
  'Simond',
  'Subea',
  'Van Rysel',
  'Solognac',
  'Inesis',

  // 🎭 Licences & Personnages (50)
  'Disney',
  'Marvel',
  'Star Wars',
  'Harry Potter',
  'Pokémon',
  'Naruto',
  'Dragon Ball',
  'One Piece',
  'My Hero Academia',
  'Demon Slayer',
  'Studio Ghibli',
  'Hello Kitty',
  'Sanrio',
  'Rilakkuma',
  'Pusheen',
  'Minecraft',
  'Fortnite',
  'Among Us',
  'Roblox',
  "Five Nights at Freddy's",
  'Sonic',
  'Mario',
  'Zelda',
  'Pokémon',
  'PAW Patrol',
  'Peppa Pig',
  'Bluey',
  'Encanto',
  'Frozen',
  'Toy Story',
  'Cars',
  'Spider-Man',
  'Batman',
  'Wonder Woman',
  'Superman',
  'Avengers',
  'Princess',
  'Unicorn',
  'Rainbow',
  'Panda',
  'Cat',
  'Dog',
  'Dinosaur',
  'Space',
  'Ocean',
  'Forest',
  'Magic',
  'Fairy',
  'Mermaid',
  'Pirate',
  'Robot',

  // 💎 Premium & Luxe (30)
  'Gucci',
  'Louis Vuitton',
  'Hermès',
  'Chanel',
  'Dior',
  'Prada',
  'Versace',
  'Armani',
  'Dolce & Gabbana',
  'Fendi',
  'Bottega Veneta',
  'Valentino',
  'Saint Laurent',
  'Givenchy',
  'Balenciaga',
  'Celine',
  'Loewe',
  'Burberry',
  'Stella McCartney',
  'Kenzo',
  'Moschino',
  'Marni',
  'Acne Studios',
  'Ganni',
  'Isabel Marant',
  'Golden Goose',
  'Stone Island',
  'Moncler',
  'Canada Goose',
  'Brunello Cucinelli',

  // 🌱 Éco-responsable (25)
  'Patagonia',
  'Bobo Choses',
  'Mini Rodini',
  'Organic Zoo',
  'Wovenplay',
  'Little Green Radicals',
  'Frugi',
  'Maxomorra',
  'DUNS Sweden',
  'Smafolk',
  'Loud + Proud',
  'Sense Organics',
  'Müsli',
  "Fred's World",
  'People Tree',
  'Thought',
  'Kotn',
  'Everlane',
  'Reformation',
  'Eileen Fisher',
  'Tentree',
  'Picture Organic',
  'Dedicated',
  'Knowledge Cotton',
  'Armedangels',

  // 🏪 Grandes Chaînes (30)
  'Primark',
  'UNIQLO',
  'C&A',
  'Reserved',
  'Cropp',
  'House',
  'Mohito',
  'Sinsay',
  'Bershka',
  'Pull & Bear',
  'Stradivarius',
  'Massimo Dutti',
  'COS',
  'Weekday',
  'Monki',
  '& Other Stories',
  'ARKET',
  'Hollister',
  'Abercrombie & Fitch',
  'American Eagle',
  'Aeropostale',
  'Forever 21',
  'Brandy Melville',
  'Urban Outfitters',
  'Anthropologie',
  'Free People',
  'Zara Home',
  'H&M Home',
  'IKEA',
  'Target',

  // 🇫🇷 Françaises Authentiques (40)
  'Bonpoint',
  'Tartine et Chocolat',
  'Jacadi',
  'IKKS',
  'Catimini',
  'Kenzo',
  'Petit Bateau',
  'Armor Lux',
  'Saint James',
  'Aigle',
  'K.Way',
  'Oxbow',
  'Picture',
  'Pyrenex',
  'Kappa',
  'Lacoste',
  'Le Coq Sportif',
  'Veja',
  'Faguo',
  'Bizzbee',
  'Jules',
  'Brice',
  'Celio',
  'Devred',
  'Père Castor',
  'Galeries Lafayette',
  'Printemps',
  'BHV',
  'Monoprix',
  'Franprix',
  'Carrefour',
  'Leclerc',
  'Auchan',
  'Intermarché',
  'Super U',
  'Systeme U',
  'Casino',
  'Cora',
  'Hyper U',
  'Match',

  // 🇺🇸 Américaines (30)
  'Gap',
  'Old Navy',
  'Banana Republic',
  'J.Crew',
  'Madewell',
  'Anthropologie',
  'Urban Outfitters',
  'American Eagle',
  'Hollister',
  'Abercrombie',
  'Tommy Hilfiger',
  'Calvin Klein',
  'Ralph Lauren',
  'Polo Ralph Lauren',
  'Brooks Brothers',
  "Levi's",
  'Wrangler',
  'Lee',
  'Dickies',
  'Carhartt',
  'Patagonia',
  'The North Face',
  'Columbia',
  'REI',
  'L.L.Bean',
  'Eddie Bauer',
  "Lands' End",
  "Carter's",
  'OshKosh',
  'Gymboree',

  // 🇮🇹 Italiennes (20)
  'Chicco',
  'Benetton',
  'Sisley',
  'Liu Jo',
  'Pinko',
  'Patrizia Pepe',
  'Elisabetta Franchi',
  'Twin-Set',
  'Blumarine',
  'Roberto Cavalli',
  'Just Cavalli',
  'Iceberg',
  'Stone Island',
  'CP Company',
  'Dsquared2',
  'Philipp Plein',
  'Golden Goose',
  'Moncler',
  'Herno',
  'Save the Duck',

  // 🇩🇪 Allemandes (20)
  'Steiff',
  'Sigikid',
  'Sanetta',
  'Schiesser',
  'Falke',
  's.Oliver',
  'Tom Tailor',
  'Esprit',
  'Cecil',
  'Street One',
  'Gerry Weber',
  'Betty Barclay',
  'Marc Cain',
  'Bogner',
  'Escada',
  'Joop!',
  'Hugo Boss',
  'Boss',
  'Adidas',
  'Puma',

  // 🇬🇧 Britanniques (20)
  'Marks & Spencer',
  'Mothercare',
  'Next',
  'Primark',
  'ASOS',
  'Boohoo',
  'PrettyLittleThing',
  'Missguided',
  'Topshop',
  'Zara',
  'River Island',
  'New Look',
  'H&M',
  'Mango',
  'Barbour',
  'Hunter',
  'Joules',
  'Seasalt',
  'White Stuff',
  'Fat Face',

  // 🇰🇷 Coréennes (15)
  'Mini Dressing',
  'Pinkfong',
  'Kakao Friends',
  'Line Friends',
  'BT21',
  'Tiny Cottons',
  'Bobo Choses',
  'The Animals Observatory',
  'Wolf & Rita',
  'Motoreta',
  'Beau Loves',
  'Tinycottons',
  'Mini Rodini',
  'Nadadelazos',
  'Wynken',

  // 🇯🇵 Japonaises (15)
  'Uniqlo',
  'Muji',
  'Studio Ghibli',
  'Hello Kitty',
  'Sanrio',
  'Rilakkuma',
  'Pokémon',
  'Nintendo',
  'Sony',
  'Bandai',
  'Takara Tomy',
  'Pilot',
  'Sailor Moon',
  'Dragon Ball',
  'Naruto',

  // 🎨 Créateurs & Niche (25)
  'Stella McCartney',
  'Kenzo',
  'Little Marc Jacobs',
  'DKNY',
  'Armani Junior',
  'Versace Young',
  'Fendi',
  'Burberry',
  'Ralph Lauren',
  'Tommy Hilfiger',
  'Calvin Klein',
  'Hugo Boss',
  'Stone Island Junior',
  'Moncler Enfant',
  'Canada Goose',
  'Parajumpers',
  'Woolrich',
  'Barbour',
  'Hunter',
  'Aigle',
  'Moon Boot',
  'UGG',
  'Timberland',
  'Dr. Martens',
  'Vans',

  // 🔄 Vintage & Seconde main (15)
  'Vintage',
  'Seconde Main',
  'Vinted',
  'Le Bon Coin',
  'Emmaus',
  'Ressourcerie',
  'Friperie',
  'Thrift',
  'Consignment',
  'Pre-owned',
  'Recyclé',
  'Upcyclé',
  'Occasion',
  'Retro',
  'Antique',

  // 🎯 Spécialisées (15)
  'Lego Wear',
  'NASA',
  'WWF',
  'UNESCO',
  'National Geographic',
  'BBC Earth',
  'Animal Planet',
  'Discovery',
  'Science Museum',
  'Louvre',
  'Metropolitan',
  'Tate',
  'MoMA',
  'Smithsonian',
  'Exploratorium',
];

// ============================================================================
// 🌈 COULEURS AVEC TRADUCTIONS i18n
// ============================================================================

const useColorTranslations = () => {
  const { t } = useTranslation('preferences-common');

  return [
    // ❤️ Rouges (10)
    { name: t('preferences.colors.red'), color: '#FF0000' },
    { name: t('preferences.colors.brightRed'), color: '#DC143C' },
    { name: t('preferences.colors.darkRed'), color: '#8B0000' },
    { name: t('preferences.colors.burgundy'), color: '#800020' },
    { name: t('preferences.colors.cherry'), color: '#DE3163' },
    { name: t('preferences.colors.raspberry'), color: '#E30B5C' },
    { name: t('preferences.colors.brickRed'), color: '#CB4154' },
    { name: t('preferences.colors.vermillion'), color: '#FF4500' },
    { name: t('preferences.colors.crimson'), color: '#DC143C' },
    { name: t('preferences.colors.scarlet'), color: '#FF2400' },

    // 🧡 Oranges (10)
    { name: t('preferences.colors.orange'), color: '#FFA500' },
    { name: t('preferences.colors.brightOrange'), color: '#FF8C00' },
    { name: t('preferences.colors.peach'), color: '#FFCBA4' },
    { name: t('preferences.colors.apricot'), color: '#FBCEB1' },
    { name: t('preferences.colors.mandarin'), color: '#FF8A65' },
    { name: t('preferences.colors.coral'), color: '#FF7F50' },
    { name: t('preferences.colors.salmon'), color: '#FA8072' },
    { name: t('preferences.colors.burntOrange'), color: '#CC5500' },
    { name: t('preferences.colors.tangerine'), color: '#F28500' },
    { name: t('preferences.colors.papaya'), color: '#FFEFD5' },

    // 💛 Jaunes (10)
    { name: t('preferences.colors.yellow'), color: '#FFFF00' },
    { name: t('preferences.colors.brightYellow'), color: '#FFD700' },
    { name: t('preferences.colors.strawYellow'), color: '#FFDB58' },
    { name: t('preferences.colors.lemon'), color: '#FFF700' },
    { name: t('preferences.colors.honey'), color: '#FFC649' },
    { name: t('preferences.colors.wheat'), color: '#F5DEB3' },
    { name: t('preferences.colors.gold'), color: '#FFD700' },
    { name: t('preferences.colors.mustard'), color: '#FFDB58' },
    { name: t('preferences.colors.canary'), color: '#FFFF99' },
    { name: t('preferences.colors.amber'), color: '#FFBF00' },

    // 💚 Verts (15)
    { name: t('preferences.colors.green'), color: '#008000' },
    { name: t('preferences.colors.brightGreen'), color: '#00FF00' },
    { name: t('preferences.colors.darkGreen'), color: '#006400' },
    { name: t('preferences.colors.lightGreen'), color: '#90EE90' },
    { name: t('preferences.colors.emerald'), color: '#50C878' },
    { name: t('preferences.colors.jade'), color: '#00A86B' },
    { name: t('preferences.colors.olive'), color: '#808000' },
    { name: t('preferences.colors.khaki'), color: '#C3B091' },
    { name: t('preferences.colors.sage'), color: '#9CAF88' },
    { name: t('preferences.colors.mint'), color: '#98FB98' },
    { name: t('preferences.colors.lime'), color: '#32CD32' },
    { name: t('preferences.colors.pistachio'), color: '#BEF574' },
    { name: t('preferences.colors.forest'), color: '#228B22' },
    { name: t('preferences.colors.seafoam'), color: '#71EEB8' },
    { name: t('preferences.colors.chartreuse'), color: '#7FFF00' },

    // 💙 Bleus (15)
    { name: t('preferences.colors.blue'), color: '#0000FF' },
    { name: t('preferences.colors.brightBlue'), color: '#0080FF' },
    { name: t('preferences.colors.darkBlue'), color: '#000080' },
    { name: t('preferences.colors.lightBlue'), color: '#ADD8E6' },
    { name: t('preferences.colors.navy'), color: '#000080' },
    { name: t('preferences.colors.royalBlue'), color: '#4169E1' },
    { name: t('preferences.colors.cyan'), color: '#00FFFF' },
    { name: t('preferences.colors.turquoise'), color: '#40E0D0' },
    { name: t('preferences.colors.aqua'), color: '#00FFFF' },
    { name: t('preferences.colors.cobalt'), color: '#0047AB' },
    { name: t('preferences.colors.indigo'), color: '#4B0082' },
    { name: t('preferences.colors.periwinkle'), color: '#CCCCFF' },
    { name: t('preferences.colors.steel'), color: '#4682B4' },
    { name: t('preferences.colors.powder'), color: '#B0E0E6' },
    { name: t('preferences.colors.electric'), color: '#7DF9FF' },

    // 💜 Violets (10)
    { name: t('preferences.colors.violet'), color: '#8A2BE2' },
    { name: t('preferences.colors.brightViolet'), color: '#9400D3' },
    { name: t('preferences.colors.purple'), color: '#800080' },
    { name: t('preferences.colors.mauve'), color: '#E0B0FF' },
    { name: t('preferences.colors.lilac'), color: '#C8A2C8' },
    { name: t('preferences.colors.lavender'), color: '#E6E6FA' },
    { name: t('preferences.colors.plum'), color: '#8E4585' },
    { name: t('preferences.colors.eggplant'), color: '#614051' },
    { name: t('preferences.colors.amethyst'), color: '#9966CC' },
    { name: t('preferences.colors.orchid'), color: '#DA70D6' },

    // 🩷 Roses (10)
    { name: t('preferences.colors.pink'), color: '#FFC0CB' },
    { name: t('preferences.colors.brightPink'), color: '#FF1493' },
    { name: t('preferences.colors.darkPink'), color: '#C21807' },
    { name: t('preferences.colors.dustyPink'), color: '#F8BBD0' },
    { name: t('preferences.colors.candyPink'), color: '#FC89AC' },
    { name: t('preferences.colors.fuchsia'), color: '#FF00FF' },
    { name: t('preferences.colors.magenta'), color: '#FF00FF' },
    { name: t('preferences.colors.antiquePink'), color: '#FAEBD7' },
    { name: t('preferences.colors.blush'), color: '#DE5D83' },
    { name: t('preferences.colors.hotPink'), color: '#FF69B4' },

    // 🤍 Neutres (10)
    { name: t('preferences.colors.white'), color: '#FFFFFF' },
    { name: t('preferences.colors.offWhite'), color: '#F5F5DC' },
    { name: t('preferences.colors.cream'), color: '#FFFDD0' },
    { name: t('preferences.colors.ivory'), color: '#FFFFF0' },
    { name: t('preferences.colors.beige'), color: '#F5F5DC' },
    { name: t('preferences.colors.sand'), color: '#C2B280' },
    { name: t('preferences.colors.taupe'), color: '#483C32' },
    { name: t('preferences.colors.mushroom'), color: '#C7B377' },
    { name: t('preferences.colors.pearl'), color: '#F0EAD6' },
    { name: t('preferences.colors.vanilla'), color: '#F3E5AB' },

    // 🩶 Gris (8)
    { name: t('preferences.colors.gray'), color: '#808080' },
    { name: t('preferences.colors.lightGray'), color: '#D3D3D3' },
    { name: t('preferences.colors.darkGray'), color: '#A9A9A9' },
    { name: t('preferences.colors.charcoal'), color: '#36454F' },
    { name: t('preferences.colors.silver'), color: '#C0C0C0' },
    { name: t('preferences.colors.slate'), color: '#708090' },
    { name: t('preferences.colors.ash'), color: '#B2BEB5' },
    { name: t('preferences.colors.smoke'), color: '#738276' },

    // 🖤 Noirs (4)
    { name: t('preferences.colors.black'), color: '#000000' },
    { name: t('preferences.colors.jetBlack'), color: '#343434' },
    { name: t('preferences.colors.carbon'), color: '#625D5D' },
    { name: t('preferences.colors.onyx'), color: '#353839' },

    // 🤎 Marrons (10)
    { name: t('preferences.colors.brown'), color: '#A52A2A' },
    { name: t('preferences.colors.lightBrown'), color: '#D2B48C' },
    { name: t('preferences.colors.darkBrown'), color: '#654321' },
    { name: t('preferences.colors.chestnut'), color: '#964B00' },
    { name: t('preferences.colors.chocolate'), color: '#7B3F00' },
    { name: t('preferences.colors.coffee'), color: '#6F4E37' },
    { name: t('preferences.colors.caramel'), color: '#C68E17' },
    { name: t('preferences.colors.hazelnut'), color: '#D2691E' },
    { name: t('preferences.colors.mahogany'), color: '#C04000' },
    { name: t('preferences.colors.copper'), color: '#B87333' },

    // ✨ Spéciaux (8)
    {
      name: t('preferences.colors.rainbow'),
      color:
        'linear-gradient(45deg, #ff0000, #ff8000, #ffff00, #80ff00, #00ff00, #00ff80, #00ffff, #0080ff, #0000ff, #8000ff, #ff00ff, #ff0080)',
    },
    {
      name: t('preferences.colors.multicolor'),
      color:
        'conic-gradient(#ff0000, #ff8000, #ffff00, #80ff00, #00ff00, #00ff80, #00ffff, #0080ff, #0000ff, #8000ff, #ff00ff, #ff0080, #ff0000)',
    },
    {
      name: t('preferences.colors.holographic'),
      color:
        'linear-gradient(45deg, #ff69b4, #87ceeb, #98fb98, #dda0dd, #f0e68c)',
    },
    { name: t('preferences.colors.metallic'), color: '#C0C0C0' },
    { name: t('preferences.colors.glittery'), color: '#FFD700' },
    {
      name: t('preferences.colors.transparent'),
      color: 'rgba(255,255,255,0.1)',
    },
    {
      name: t('preferences.colors.iridescent'),
      color: 'linear-gradient(45deg, #ff00ff, #00ffff, #ff00ff)',
    },
    { name: t('preferences.colors.neon'), color: '#39FF14' },
  ];
};

// ============================================================================
// ✨ STYLES AVEC TRADUCTIONS i18n
// ============================================================================

const useStyleTranslations = () => {
  const { t } = useTranslation('preferences-common');

  return [
    // 👗 Basiques
    { name: t('preferences.styles.casual'), icon: '👕' },
    { name: t('preferences.styles.chic'), icon: '✨' },
    { name: t('preferences.styles.elegant'), icon: '💫' },
    { name: t('preferences.styles.classic'), icon: '👔' },
    { name: t('preferences.styles.modern'), icon: '🔥' },
    { name: t('preferences.styles.minimalist'), icon: '⚪' },
    { name: t('preferences.styles.simple'), icon: '🤍' },
    { name: t('preferences.styles.sophisticated'), icon: '💎' },

    // 🎭 Personnalités
    { name: t('preferences.styles.bohemian'), icon: '🌸' },
    { name: t('preferences.styles.vintage'), icon: '📻' },
    { name: t('preferences.styles.retro'), icon: '🕺' },
    { name: t('preferences.styles.grunge'), icon: '🖤' },
    { name: t('preferences.styles.punk'), icon: '🔗' },
    { name: t('preferences.styles.rock'), icon: '🎸' },
    { name: t('preferences.styles.gothic'), icon: '🦇' },
    { name: t('preferences.styles.romantic'), icon: '💕' },

    // 👑 Enfants spéciaux
    { name: t('preferences.styles.princess'), icon: '👑' },
    { name: t('preferences.styles.prince'), icon: '🤴' },
    { name: t('preferences.styles.fairy'), icon: '🧚' },
    { name: t('preferences.styles.unicorn'), icon: '🦄' },
    { name: t('preferences.styles.mermaid'), icon: '🧜' },
    { name: t('preferences.styles.pirate'), icon: '🏴‍☠️' },
    { name: t('preferences.styles.ninja'), icon: '🥷' },
    { name: t('preferences.styles.cowboy'), icon: '🤠' },

    // 🏃 Activités
    { name: t('preferences.styles.sport'), icon: '🏃' },
    { name: t('preferences.styles.athletic'), icon: '⚽' },
    { name: t('preferences.styles.dance'), icon: '💃' },
    { name: t('preferences.styles.yoga'), icon: '🧘' },
    { name: t('preferences.styles.running'), icon: '🏃‍♀️' },
    { name: t('preferences.styles.fitness'), icon: '💪' },
    { name: t('preferences.styles.outdoor'), icon: '🏔️' },
    { name: t('preferences.styles.beach'), icon: '🏖️' },

    // 🎨 Artistiques
    { name: t('preferences.styles.kawaii'), icon: '🥺' },
    { name: t('preferences.styles.manga'), icon: '🌸' },
    { name: t('preferences.styles.anime'), icon: '👾' },
    { name: t('preferences.styles.korean'), icon: '🌟' },
    { name: t('preferences.styles.japanese'), icon: '🎌' },
    { name: t('preferences.styles.harajuku'), icon: '🌈' },
    { name: t('preferences.styles.lolita'), icon: '🎀' },
    { name: t('preferences.styles.decora'), icon: '🎭' },

    // 🌍 Culturels
    { name: t('preferences.styles.ethnic'), icon: '🌍' },
    { name: t('preferences.styles.tribal'), icon: '🪶' },
    { name: t('preferences.styles.african'), icon: '🌍' },
    { name: t('preferences.styles.indian'), icon: '🇮🇳' },
    { name: t('preferences.styles.asian'), icon: '🏮' },
    { name: t('preferences.styles.scandinavian'), icon: '❄️' },
    { name: t('preferences.styles.mediterranean'), icon: '🌊' },
    { name: t('preferences.styles.american'), icon: '🇺🇸' },

    // 🎉 Occasions
    { name: t('preferences.styles.party'), icon: '🌙' },
    { name: t('preferences.styles.celebration'), icon: '🎉' },
    { name: t('preferences.styles.wedding'), icon: '💒' },
    { name: t('preferences.styles.cocktail'), icon: '🍸' },
    { name: t('preferences.styles.business'), icon: '💼' },
    { name: t('preferences.styles.relaxed'), icon: '😌' },
    { name: t('preferences.styles.weekend'), icon: '🏡' },
    { name: t('preferences.styles.vacation'), icon: '✈️' },

    // 🌈 Moods
    { name: t('preferences.styles.colorful'), icon: '🌈' },
    { name: t('preferences.styles.flashy'), icon: '⚡' },
    { name: t('preferences.styles.pastel'), icon: '🌸' },
    { name: t('preferences.styles.neutral'), icon: '🤍' },
    { name: t('preferences.styles.monochrome'), icon: '⚫' },
    { name: t('preferences.styles.contrasted'), icon: '⚫⚪' },
    { name: t('preferences.styles.harmonious'), icon: '🎨' },
    { name: t('preferences.styles.bold'), icon: '🔥' },

    // ✨ Spéciaux
    { name: t('preferences.styles.glittery'), icon: '✨' },
    { name: t('preferences.styles.shiny'), icon: '💎' },
    { name: t('preferences.styles.metallic'), icon: '🔘' },
    { name: t('preferences.styles.holographic'), icon: '🌈' },
    { name: t('preferences.styles.transparent'), icon: '🔮' },
    { name: t('preferences.styles.textured'), icon: '📐' },
  ];
};

// ============================================================================
// 🎨 COMPOSANT PRINCIPAL
// ============================================================================

const AutocompletePreferences: React.FC<AutocompletePreferencesProps> = ({
  type,
  selectedItems,
  onAdd,
  onRemove,
  placeholder,
}) => {
  const { t } = useTranslation('preferences-common');
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 🌐 Traductions
  const translatedColors = useColorTranslations();
  const translatedStyles = useStyleTranslations();

  // 📊 Data selon le type
  const getAllItems = () => {
    switch (type) {
      case 'brand':
        return ALL_BRANDS;
      case 'color':
        return translatedColors;
      case 'style':
        return translatedStyles;
      default:
        return [];
    }
  };

  const allItems = getAllItems();

  // 🔍 Filtrage intelligent
  const getFilteredItems = () => {
    if (!inputValue.trim()) {
      // Suggestions populaires quand pas de recherche
      const popular =
        type === 'brand'
          ? [
              'Zara',
              'Nike',
              'Disney',
              'H&M',
              'Adidas',
              'Marvel',
              'Gucci',
              'Pokémon',
            ]
          : type === 'color'
            ? translatedColors.slice(0, 8)
            : translatedStyles.slice(0, 8);
      return popular;
    }

    const query = inputValue.toLowerCase().trim();
    return allItems
      .filter(item => {
        const itemName = typeof item === 'string' ? item : item.name;
        return (
          itemName.toLowerCase().includes(query) &&
          !selectedItems.includes(itemName)
        );
      })
      .slice(0, 10);
  };

  const filteredItems = getFilteredItems();

  // ⌨️ Navigation clavier
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredItems.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredItems.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelectItem(filteredItems[highlightedIndex]);
        } else if (inputValue.trim()) {
          handleSelectItem(inputValue.trim());
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // 🎯 Sélection item
  const handleSelectItem = (item: string | ColorItem | StyleItem) => {
    const itemName = typeof item === 'string' ? item : item.name;
    if (!selectedItems.includes(itemName)) {
      onAdd(itemName);
    }
    setInputValue('');
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  // 🎨 Rendu pastille couleur
  const renderColorDot = (colorItem: ColorItem) => {
    const isGradient =
      colorItem.color.includes('gradient') || colorItem.color.includes('conic');

    return (
      <div
        className="w-3 h-3 rounded-full border border-brand-border mr-2 flex-shrink-0"
        style={{
          background: colorItem.color,
          ...(isGradient && {
            animation: 'rainbow 3s ease-in-out infinite alternate',
            filter: 'hue-rotate(0deg)',
          }),
        }}
      />
    );
  };

  // 🎨 Icône selon le type avec traductions
  const getTypeIcon = () => {
    switch (type) {
      case 'brand':
        return '🏷️';
      case 'color':
        return '🌈';
      case 'style':
        return '✨';
      default:
        return '📝';
    }
  };

  const getTypeLabel = () => {
    switch (type) {
      case 'brand':
        return t('preferences.labels.brands');
      case 'color':
        return t('preferences.labels.colors');
      case 'style':
        return t('preferences.labels.styles');
      default:
        return '';
    }
  };

  // 📱 Effet pour fermer dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 🎯 Focus management
  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[
        highlightedIndex
      ] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  return (
    <div className="relative">
      {/* 🏷️ Label avec icône et traduction */}
      <label
        className="block text-sm font-medium mb-2"
        style={{ color: 'var(--brand-warm)' }}
      >
        {getTypeIcon()} {getTypeLabel()}
      </label>

      {/* 🎯 Tags sélectionnés */}
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {selectedItems.map(item => {
            const colorItem =
              type === 'color'
                ? translatedColors.find(c => c.name === item)
                : null;
            const styleItem =
              type === 'style'
                ? translatedStyles.find(s => s.name === item)
                : null;

            return (
              <span
                key={item}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border"
                style={{
                  backgroundColor: 'var(--brand-light)',
                  color: 'var(--brand-warm)',
                  borderColor: 'var(--brand-border)',
                }}
              >
                {type === 'color' && colorItem && renderColorDot(colorItem)}
                {type === 'style' && styleItem && (
                  <span className="mr-1">{styleItem.icon}</span>
                )}
                <span>{item}</span>
                <button
                  type="button"
                  onClick={() => onRemove(item)}
                  className="ml-1 hover:bg-red-100 rounded-full p-0.5 transition-colors"
                  style={{ color: 'var(--brand-warm)' }}
                  aria-label={t('preferences.actions.remove')}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* 🔍 Input avec dropdown */}
      <div className="relative" ref={dropdownRef}>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={e => {
            setInputValue(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors"
          style={{
            backgroundColor: 'var(--brand-offwhite)',
            borderColor: isOpen ? 'var(--brand-500)' : '#D4C4A8',
            color: 'var(--brand-warm)',
            boxShadow: isOpen ? '0 0 0 3px rgba(200, 168, 130, 0.1)' : 'none',
          }}
        />

        {/* 🎯 Dropdown suggestions */}
        {isOpen && (
          <div
            className="absolute z-50 w-full mt-1 border rounded-lg shadow-lg max-h-60 overflow-y-auto"
            style={{
              backgroundColor: 'var(--brand-offwhite)',
              borderColor: 'var(--brand-border)',
            }}
          >
            {filteredItems.length === 0 ? (
              <div
                className="px-3 py-2 text-sm"
                style={{ color: 'var(--brand-warm)' }}
              >
                {inputValue.trim()
                  ? t('preferences.search.noResults')
                  : t('preferences.search.startTyping')}
              </div>
            ) : (
              <>
                {!inputValue.trim() && (
                  <div
                    className="px-3 py-1 text-xs font-medium border-b"
                    style={{
                      color: 'var(--brand-warm)',
                      backgroundColor: 'var(--brand-light)',
                      borderColor: 'var(--brand-border)',
                    }}
                  >
                    💡 {t('preferences.search.popularSuggestions')}
                  </div>
                )}
                {filteredItems.map((item, index) => {
                  const itemName = typeof item === 'string' ? item : item.name;
                  const colorItem =
                    type === 'color' && typeof item === 'object'
                      ? (item as ColorItem)
                      : null;
                  const styleItem =
                    type === 'style' && typeof item === 'object'
                      ? (item as StyleItem)
                      : null;

                  return (
                    <button
                      key={itemName}
                      type="button"
                      onClick={() => handleSelectItem(item)}
                      className={`w-full px-3 py-2 text-left text-sm hover:transition-colors flex items-center ${
                        index === highlightedIndex ? 'font-medium' : ''
                      }`}
                      style={{
                        backgroundColor:
                          index === highlightedIndex
                            ? '#F4F1EB'
                            : 'transparent',
                        color: 'var(--brand-warm)',
                      }}
                    >
                      {colorItem && renderColorDot(colorItem)}
                      {styleItem && (
                        <span className="mr-2">{styleItem.icon}</span>
                      )}
                      <span>{itemName}</span>
                    </button>
                  );
                })}
                {inputValue.trim() &&
                  !filteredItems.some(item => {
                    const itemName =
                      typeof item === 'string' ? item : item.name;
                    return itemName.toLowerCase() === inputValue.toLowerCase();
                  }) && (
                    <button
                      type="button"
                      onClick={() => handleSelectItem(inputValue.trim())}
                      className="w-full px-3 py-2 text-left text-sm border-t font-medium"
                      style={{
                        color: 'var(--brand-warm)',
                        backgroundColor: 'var(--brand-light)',
                        borderColor: 'var(--brand-border)',
                      }}
                    >
                      ➕ {t('preferences.actions.add')} "{inputValue.trim()}"
                    </button>
                  )}
              </>
            )}
          </div>
        )}
      </div>

      {/* 💡 Info helper avec traductions */}
      <p className="text-xs mt-1" style={{ color: 'var(--brand-warm)' }}>
        {type === 'brand'
          ? t('preferences.help.brands')
          : type === 'color'
            ? t('preferences.help.colors')
            : t('preferences.help.styles')}{' '}
        • {t('preferences.help.navigation')}
      </p>

      {/* ✨ CSS pour animations rainbow */}
      <style>{`
          @keyframes rainbow {
            0% { filter: hue-rotate(0deg); }
            100% { filter: hue-rotate(360deg); }
          }
      `}</style>
    </div>
  );
};

export default AutocompletePreferences;
